import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { qaqcApi, submittalsApi, type MixDesign, type PourCard } from '@/lib/apiClient';
import { Button } from '@/components';

/**
 * The job's approved concrete mix designs — the register incoming pour cards are
 * matched against.
 *
 * Owned by the PM (Director of Preconstruction, 2026-07-29: "Typically the PM is
 * responsible for sending the concrete submittals and confirming approved Mix
 * IDs"), so it's gated on the projects.edit they already hold rather than a new
 * permission. Field leaders see it read-only, which is the right split: they
 * report the mix they were handed, the PM decides what counts as approved.
 */
export default function MixDesignsCard({ projectId, canEdit, cards }: {
  projectId: number; canEdit: boolean; cards: PourCard[];
}) {
  const qc = useQueryClient();
  const [showRetired, setShowRetired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [prefill, setPrefill] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  // ALWAYS fetch retired rows too, and filter for display here.
  //
  // The first version passed `showRetired` to the API and then gated the toggle
  // on `designs.some(d => !d.isActive)` — which could never be true while the API
  // was filtering them out, so the toggle never appeared, so it could never be
  // switched on, so Reinstate was unreachable. Fetching everything makes the
  // retired count honest and the control reachable.
  const query = useQuery({
    queryKey: ['qaqc-mix-designs', projectId, 'all'],
    queryFn: () => qaqcApi.listMixDesigns(projectId, true),
  });
  const all = query.data ?? [];
  const active = all.filter((d) => d.isActive);
  const retiredCount = all.length - active.length;
  const designs = showRetired ? all : active;

  // Mixes the field is already pouring that AREN'T on the register. Filling the
  // register in from what's actually being used is far quicker than typing it
  // from the submittal log, and it's the same list the flags come from.
  const unapproved = useMemo(() => {
    const seen = new Map<string, number>();
    for (const c of cards) {
      if (c.mixMatch !== 'unapproved' || !c.mix) continue;
      seen.set(c.mix, (seen.get(c.mix) ?? 0) + 1);
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1]);
  }, [cards]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['qaqc-mix-designs'] });
    // Verdicts are computed on read, so the cards have to refetch for badges to clear.
    qc.invalidateQueries({ queryKey: ['qaqc-pours'] });
  };
  const retireMut = useMutation({ mutationFn: (id: number) => qaqcApi.retireMixDesign(id), onSuccess: invalidate });
  const reinstateMut = useMutation({ mutationFn: (id: number) => qaqcApi.reinstateMixDesign(id), onSuccess: invalidate });

  const openAdd = (seed = '') => { setPrefill(seed); setEditingId(null); setAdding(true); };
  const closeAdd = () => { setAdding(false); setPrefill(''); };

  return (
    <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
      <h2 className="pw-card-hd" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <span>Approved Mix Designs</span>
        {canEdit && !adding && (
          <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }}
                  onClick={() => openAdd()}>
            + Approve a mix
          </button>
        )}
      </h2>

      <div style={{ fontSize: 12, color: 'var(--ew-n-500)', padding: '10px var(--ew-s-4) 0' }}>
        Mix IDs approved for this job. Pour cards arriving from the field are checked against this list, and anything not on it
        is flagged — so keep it current as concrete submittals come back approved.
        {active.length === 0 && (
          <div style={{ marginTop: 8, padding: '8px 11px', background: 'var(--ew-quality-50)', border: '1px solid var(--ew-quality-100)', borderRadius: 'var(--ew-r-md)', color: 'var(--ew-quality-700)' }}>
            <b>Nothing approved yet, so no pour is being checked.</b> Mixes stay unverified until at least one is on this list.
            That's deliberate — an empty register flagging every pour would just teach everyone to ignore the flag.
          </div>
        )}
      </div>

      {/* Fast path: approve a mix the field is already using. */}
      {canEdit && unapproved.length > 0 && (
        <div style={{ margin: '10px var(--ew-s-4) 0', padding: '9px 11px', background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12, color: 'var(--ew-safety-700)' }}>
          <div style={{ marginBottom: 6 }}>
            {unapproved.length === 1 ? 'One mix on this job isn’t' : `${unapproved.length} mixes on this job aren’t`} on the approved list —
            click to approve, or fix the typo on the pour card:
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {unapproved.map(([mix, n]) => (
              <button key={mix} onClick={() => openAdd(mix)}
                      title={`Used on ${n} pour card${n === 1 ? '' : 's'} — click to approve it for this job`}
                      style={{ cursor: 'pointer', background: '#fff', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-pill)', padding: '3px 10px', font: 'var(--ew-fw-semibold) 11px var(--ew-font)', color: 'var(--ew-safety-700)' }}>
                {mix} <span style={{ opacity: 0.7, fontWeight: 400 }}>×{n}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {adding && <MixForm projectId={projectId} prefill={prefill} onDone={closeAdd} onSaved={invalidate} />}

      {designs.length === 0 && !adding ? (
        <div style={{ padding: 'var(--ew-s-4)', fontSize: 13, color: 'var(--ew-n-500)' }}>
          No mix designs recorded for this job yet.
        </div>
      ) : (
        <div className="ew-table-scroll">
          <table className="qq-table ew-cards">
            <thead>
              <tr>
                <th>Mix ID</th>
                <th>Description</th>
                <th>Strength</th>
                <th>Supplier</th>
                <th>Submittal</th>
                <th>Approved</th>
                {canEdit ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {designs.map((d) => editingId === d.id ? (
                <tr key={d.id}>
                  <td colSpan={canEdit ? 7 : 6} style={{ padding: 0 }}>
                    <MixForm projectId={projectId} existing={d} onDone={() => setEditingId(null)} onSaved={invalidate} />
                  </td>
                </tr>
              ) : (
                <tr key={d.id} style={d.isActive ? undefined : { opacity: 0.55 }}>
                  <td data-label="Mix ID" style={{ fontWeight: 600 }}>
                    {d.mixId}
                    {!d.isActive && (
                      <span className="qq-chip" style={{ marginLeft: 8 }}
                            title={d.retiredAt ? `Retired ${new Date(d.retiredAt).toLocaleDateString()} — pours that already matched it keep their match` : 'Pours that already matched it keep their match'}>
                        Retired
                      </span>
                    )}
                  </td>
                  <td className="qq-muted" data-label="Description">{d.description ?? '—'}</td>
                  <td className="qq-muted" data-label="Strength" style={{ whiteSpace: 'nowrap' }}>{d.strengthPsi ? `${d.strengthPsi.toLocaleString()} psi` : '—'}</td>
                  <td className="qq-muted" data-label="Supplier">{d.supplier ?? '—'}</td>
                  <td className="qq-muted" data-label="Submittal">
                    {d.submittalNumber
                      ? <span title={d.submittalTitle ?? undefined}>{d.submittalNumber}</span>
                      : <span style={{ color: 'var(--ew-n-400)' }}>not linked</span>}
                  </td>
                  <td className="qq-muted" data-label="Approved" style={{ whiteSpace: 'nowrap' }}>
                    {d.approvedOn
                      ? new Date(d.approvedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                      : '—'}
                  </td>
                  {canEdit ? (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <Button variant="secondary" size="sm" onClick={() => { setEditingId(d.id); closeAdd(); }}>Edit</Button>
                        {d.isActive ? (
                          <Button variant="secondary" size="sm" isLoading={retireMut.isPending}
                                  onClick={() => { if (confirm(`Retire ${d.mixId}? Pours that already matched it keep their match.`)) retireMut.mutate(d.id); }}>
                            Retire
                          </Button>
                        ) : (
                          <Button variant="secondary" size="sm" isLoading={reinstateMut.isPending} onClick={() => reinstateMut.mutate(d.id)}>
                            Reinstate
                          </Button>
                        )}
                      </span>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ padding: '10px var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)', font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span>Matching ignores case, spaces and punctuation — <code>408SP2NH</code>, <code>408-SP2NH</code> and <code>408 sp2nh</code> all count as the same mix.</span>
        {retiredCount > 0 ? (
          <button className="ew-btn-link" style={{ color: 'var(--ew-green)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }}
                  onClick={() => setShowRetired((s) => !s)}>
            {showRetired ? 'Hide retired' : `Show retired (${retiredCount})`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Add / edit form ─────────────────────────────────────────────────────────

function MixForm({ projectId, existing, prefill, onDone, onSaved }: {
  projectId: number;
  existing?: MixDesign;
  prefill?: string;
  onDone: () => void;
  onSaved: () => void;
}) {
  const [mixId, setMixId] = useState(existing?.mixId ?? prefill ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [strengthPsi, setStrengthPsi] = useState(existing?.strengthPsi ? String(existing.strengthPsi) : '');
  const [supplier, setSupplier] = useState(existing?.supplier ?? '');
  const [approvedOn, setApprovedOn] = useState(existing?.approvedOn ? existing.approvedOn.slice(0, 10) : '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  // Submittal link: a real <select> keyed on the submittal ID, NOT free text.
  //
  // It was a Combobox, which keeps whatever you type — so anything that wasn't an
  // exact label match silently saved as "no link", with no error and no sign it
  // hadn't worked. Worse, the box was seeded from `submittalNumber`, so a
  // submittal without a number (the column is nullable) seeded blank and saving
  // any other field silently DROPPED an existing link. Keying on the id makes the
  // link either real or visibly absent, and round-trips correctly.
  const [submittalId, setSubmittalId] = useState<string>(existing?.submittalId != null ? String(existing.submittalId) : '');
  const [error, setError] = useState<string | null>(null);

  // Only Mix Design submittals would be ideal, but the type isn't stored
  // separately, so offer them all and let the PM choose.
  const { data: submittals } = useQuery({
    queryKey: ['submittals', projectId],
    queryFn: () => submittalsApi.list({ projectId }),
    staleTime: 60_000,
  });
  const submittalOptions = useMemo(
    () => (submittals ?? []).map((s) => ({
      id: s.id,
      label: `${s.submittalNumber ?? `#${s.id}`} — ${s.title}`,
    })),
    [submittals],
  );
  // A design can point at a submittal that's since been soft-deleted, or one this
  // list doesn't carry. Keep it selectable rather than silently clearing it.
  const orphanedLink = existing?.submittalId != null
    && !submittalOptions.some((o) => o.id === existing.submittalId);

  const save = useMutation({
    mutationFn: () => {
      const psi = strengthPsi.trim() === '' ? null : Number(strengthPsi);
      if (psi !== null && !Number.isFinite(psi)) throw new Error('Strength must be a number');
      const body = {
        mixId: mixId.trim(),
        description: description.trim() || null,
        strengthPsi: psi,
        supplier: supplier.trim() || null,
        submittalId: submittalId === '' ? null : Number(submittalId),
        approvedOn: approvedOn || null,
        notes: notes.trim() || null,
      };
      return existing
        ? qaqcApi.updateMixDesign(existing.id, body)
        : qaqcApi.createMixDesign({ projectId, ...body });
    },
    onSuccess: () => { onSaved(); onDone(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Failed to save'),
  });

  const field: React.CSSProperties = {
    border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 9px',
    font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff', color: 'var(--ew-dark)', width: '100%',
  };
  const label: React.CSSProperties = { font: 'var(--ew-fw-semibold) 10.5px/1 var(--ew-font)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ew-n-500)', display: 'block', marginBottom: 4 };

  return (
    <div style={{ padding: 'var(--ew-s-4)', background: 'var(--ew-n-50, #FAFAFA)', borderTop: '1px solid var(--ew-n-100)', borderBottom: '1px solid var(--ew-n-100)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <label style={label}>Mix ID *</label>
          <input style={field} value={mixId} onChange={(e) => setMixId(e.target.value)} placeholder="408SP2NH" autoFocus />
        </div>
        <div>
          <label style={label}>Description</label>
          <input style={field} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="4000 psi, 3/4&quot; limestone" />
        </div>
        <div>
          <label style={label}>Strength (psi)</label>
          <input style={field} value={strengthPsi} onChange={(e) => setStrengthPsi(e.target.value)} inputMode="numeric" placeholder="4000" />
        </div>
        <div>
          <label style={label}>Supplier</label>
          <input style={field} value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Irving Materials" />
        </div>
        <div>
          <label style={label}>Approved on</label>
          <input style={field} type="date" value={approvedOn} onChange={(e) => setApprovedOn(e.target.value)} />
        </div>
        <div>
          <label style={label}>Submittal (optional)</label>
          <select style={field} value={submittalId} onChange={(e) => setSubmittalId(e.target.value)}>
            <option value="">Not linked</option>
            {orphanedLink && (
              <option value={String(existing!.submittalId)}>
                {existing!.submittalNumber ?? `#${existing!.submittalId}`} — {existing!.submittalTitle ?? 'linked submittal'}
              </option>
            )}
            {submittalOptions.map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
          </select>
          {submittalOptions.length === 0 && (
            <div style={{ font: '11px var(--ew-font)', color: 'var(--ew-n-400)', marginTop: 3 }}>
              No submittals logged on this job yet — the mix can be approved without one.
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={label}>Notes</label>
        <input style={field} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the office should know about this mix" />
      </div>

      {error && (
        <div style={{ margin: '10px 0 0', padding: '7px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <Button variant="secondary" size="sm" onClick={onDone} disabled={save.isPending}>Cancel</Button>
        <Button variant="primary" size="sm" isLoading={save.isPending}
                onClick={() => { setError(null); save.mutate(); }}
                disabled={mixId.trim() === ''}>
          {existing ? 'Save changes' : 'Approve mix'}
        </Button>
      </div>
    </div>
  );
}
